import { MOCK_OCCUPATIONS, MOCK_OCCUPATION_DATASETS, MOCK_OCCUPATION_TITLES, type MockOccupationDataset, type MockOccupationRecord } from './mockOccupationData';
import type { OccupationDataset, OccupationItem, OccupationMatchKind, OccupationSearchParams, OccupationSearchResult, OccupationSystem } from './guidedTypes';

/**
 * The mock's occupation lookup over the small REAL subset in mockOccupationData.ts (never an invented occupation). It follows the contract
 * (docs/job-role-guided-fields-contract.md 2.1): case-insensitive search over code (exact, then prefix), title (prefix, then contains) and reported
 * titles (contains); ranking exact code, code prefix, title prefix, title contains, synonym contains, ties by title then code; each occupation once with
 * its best match; a blank search returns the first `take` in code (major-group) order; `socCode` restricts an O*NET search.
 *
 * `epn.mock.occupations = none` simulates an environment where no dataset has been imported.
 */
export const MOCK_OCCUPATIONS_KEY = 'epn.mock.occupations';

const DEFAULT_TAKE = 20;
const MAX_TAKE = 50;

function readKey(key: string): string | null {
  try {
    return globalThis.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export const noOccupationDataset = (): boolean => readKey(MOCK_OCCUPATIONS_KEY) === 'none';

function toDataset(d: MockOccupationDataset, isCurrent = true): OccupationDataset {
  return {
    system: d.system, name: d.name, version: d.version, sourceOrganization: d.sourceOrganization, sourceUrl: d.sourceUrl, licenseName: d.licenseName,
    licenseUrl: d.licenseUrl, attributionText: d.attributionText, limitation: d.limitation, retrievedOn: d.retrievedOn, isCurrent,
  };
}

export function mockOccupationDataset(system: OccupationSystem): OccupationDataset | null {
  if (noOccupationDataset()) return null;
  const found = MOCK_OCCUPATION_DATASETS.find((d) => d.system === system);
  return found ? toDataset(found) : null;
}

export function mockOccupationDatasets(): OccupationDataset[] {
  return noOccupationDataset() ? [] : MOCK_OCCUPATION_DATASETS.map((d) => toDataset(d));
}

const RANK: Record<string, number> = { codeExact: 0, codePrefix: 1, titlePrefix: 2, titleContains: 3, synonym: 4 };

function shape(o: MockOccupationRecord, matchedOn: OccupationMatchKind | null, matchedText: string | null): OccupationItem {
  return {
    code: o.code, title: o.title, description: o.description, majorGroupCode: o.majorGroupCode, majorGroupName: o.majorGroupName, socCode: o.socCode, matchedOn,
    matchedText,
  };
}

/** The records of one system, in code order. */
const ofSystem = (system: OccupationSystem): MockOccupationRecord[] => MOCK_OCCUPATIONS.filter((o) => o.system === system).sort((a, b) => a.code.localeCompare(b.code));

export function mockSearchOccupations(params: OccupationSearchParams): OccupationSearchResult {
  const system = params.system;
  const dataset = mockOccupationDataset(system);
  if (!dataset) return { system, dataset: null, items: [], totalMatches: 0 };
  const take = Math.min(MAX_TAKE, Math.max(1, params.take ?? DEFAULT_TAKE));
  let pool = ofSystem(system);
  if (system === 'Onet' && params.socCode) pool = pool.filter((o) => o.socCode === params.socCode);
  const q = (params.search ?? '').trim().toLowerCase();
  // Like the server: a blank search reports every item as a title match.
  if (!q) return { system, dataset, items: pool.slice(0, take).map((o) => shape(o, 'Title', null)), totalMatches: pool.length };

  const synonyms = new Map<string, string[]>();
  if (system === 'Onet') for (const t of MOCK_OCCUPATION_TITLES) synonyms.set(t.onetCode, [...(synonyms.get(t.onetCode) ?? []), t.title]);
  else {
    // A SOC occupation is found through the reported titles of its O*NET children too (contract: "reported titles, SOC and O*NET").
    for (const o of MOCK_OCCUPATIONS) {
      if (o.system !== 'Onet') continue;
      const titles = MOCK_OCCUPATION_TITLES.filter((t) => t.onetCode === o.code).map((t) => t.title);
      synonyms.set(o.socCode, [...(synonyms.get(o.socCode) ?? []), ...titles]);
    }
  }

  const scored: { record: MockOccupationRecord; rank: number; kind: OccupationMatchKind; text: string | null }[] = [];
  for (const o of pool) {
    const code = o.code.toLowerCase();
    const title = o.title.toLowerCase();
    let hit: { rank: number; kind: OccupationMatchKind; text: string | null } | null = null;
    if (code === q) hit = { rank: RANK.codeExact!, kind: 'Code', text: null };
    else if (code.startsWith(q)) hit = { rank: RANK.codePrefix!, kind: 'Code', text: null };
    else if (title.startsWith(q)) hit = { rank: RANK.titlePrefix!, kind: 'Title', text: null };
    else if (title.includes(q)) hit = { rank: RANK.titleContains!, kind: 'Title', text: null };
    else {
      const reported = (synonyms.get(o.code) ?? []).find((t) => t.toLowerCase().includes(q));
      if (reported) hit = { rank: RANK.synonym!, kind: 'Synonym', text: reported };
    }
    if (hit) scored.push({ record: o, ...hit });
  }
  scored.sort((a, b) => a.rank - b.rank || a.record.title.localeCompare(b.record.title) || a.record.code.localeCompare(b.record.code));
  return { system, dataset, items: scored.slice(0, take).map((s) => shape(s.record, s.kind, s.text)), totalMatches: scored.length };
}

export function mockGetOccupation(system: OccupationSystem, code: string): OccupationItem | null {
  if (noOccupationDataset()) return null;
  const found = MOCK_OCCUPATIONS.find((o) => o.system === system && o.code === code);
  // Like the server: the detail has the list-item shape, matched on its code.
  return found ? shape(found, 'Code', null) : null;
}

/** Whether `code` exists in the current mock dataset of `system` (true when no dataset exists: only the format check applies then, like the server). */
export function mockOccupationExists(system: OccupationSystem, code: string): boolean {
  if (noOccupationDataset()) return true;
  return MOCK_OCCUPATIONS.some((o) => o.system === system && o.code === code);
}
